import {
  createEntityMediaService,
  type EntityMedia,
  type EntityMediaSummary,
  type EntityMediaType,
  type EntityMediaUploadResult,
} from '@/shared/media'

const DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
]

function attachmentTypeFor(file: File): EntityMediaType {
  if (file.type === 'application/pdf') return 'pdf'

  if (
    file.type === 'application/msword' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.name.toLowerCase().endsWith('.doc') ||
    file.name.toLowerCase().endsWith('.docx')
  ) {
    return 'document'
  }

  if (
    file.type === 'application/vnd.ms-excel' ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'text/csv' ||
    file.name.toLowerCase().endsWith('.xls') ||
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.name.toLowerCase().endsWith('.csv')
  ) {
    return 'spreadsheet'
  }

  return 'other'
}

const batchAttachmentService = createEntityMediaService({
  scopeValue: 'asset_holding',
  typeValues: ['pdf', 'document', 'spreadsheet', 'other'],
  allowedTypes: DOCUMENT_TYPES,
  allowedExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'],
  resolveFileType: attachmentTypeFor,
  serviceLabel: 'entity document attachment',
})

export type BatchAttachment = EntityMedia
export type BatchAttachmentUploadResult = EntityMediaUploadResult
export type BatchAttachmentPreview = EntityMediaSummary['previews'][number]
export type BatchAttachmentSummary = EntityMediaSummary

export async function uploadBatchAttachment(
  unitId: string,
  holdingId: string,
  file: File,
  attachmentName?: string,
): Promise<BatchAttachmentUploadResult> {
  return batchAttachmentService.uploadEntityMedia({
    unitId,
    entityId: holdingId,
    file,
    mediaName: attachmentName,
  })
}

export async function getBatchAttachments(holdingId: string): Promise<BatchAttachment[]> {
  return batchAttachmentService.getEntityMedia(holdingId)
}

export async function getBatchAttachmentSummaries(holdingIds: string[]): Promise<Record<string, BatchAttachmentSummary>> {
  return batchAttachmentService.getEntityMediaSummaries(holdingIds)
}

export async function getBatchAttachmentUrl(attachmentPath: string): Promise<string> {
  return batchAttachmentService.getEntityMediaUrl(attachmentPath)
}

export async function updateBatchAttachmentName(attachmentId: string, attachmentName: string): Promise<BatchAttachment> {
  return batchAttachmentService.updateEntityMediaName(attachmentId, attachmentName)
}

export async function deleteBatchAttachment(attachmentId: string, attachmentPath: string): Promise<void> {
  return batchAttachmentService.deleteEntityMedia(attachmentId, attachmentPath)
}

export { batchAttachmentService }
